<template>
  <div>
    <div class="page-wrapper">
      <div class="container-fluid p-0">
        <div class="row justify-content-center align-items-center min-vh-100">
          <div class="col-10 col-md-6 col-lg-4">
            <div class="text-center logo d-block mb-4">
              <img class="img-fluid" src="../../../assets/images/logo/sfa_logo.svg" alt="SFA Logo" style="max-width: 200px;">
            </div>

            <div class="form-container">
              <h4 class="text-center p-b-10">Reset Password</h4>
              <form @submit.prevent="handleChangePassword">
                <!--Add New Password Textbox-->
                <div class="form-group">
                  <label for="newPassword">New Password <span>*</span></label>
                  <div class="input-group">
                    <input
                        id="newPassword"
                        :type="showNewPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="form.newPassword"
                        @input="evaluateNewPassword"
                        required
                    />
                    <!--Password Show and Hide Button-->
                    <button type="button" style="width:50px; border: 1px solid #dee2e6; border-radius: 0 5px 5px 0; background-color: #fff;" @click="toggleShowNewPassword">
                      <i :class="showNewPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                    </button>
                  </div>
                  <!--Password Policy Checking and Password Strength Indicators-->
                  <div v-if="form.newPassword.length !== 0" class="progress mt-2" style="height: 5px; border-radius: 5px;">
                    <div :class="['progress-bar', newPasswordStrengthLabel === 'Strong' ? 'bg-success' : 'bg-danger']" :style="{ width: newPasswordStrength + '%' }"></div>
                  </div>
                  <div v-if="form.newPassword.length !== 0" class="d-flex justify-content-between mt-1">
                    <small :class="newPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-start">
                      {{ newPasswordErrors }}
                    </small>
                    <small :class="newPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-end">
                      {{ newPasswordStrengthLabel }}
                    </small>
                  </div>
                </div>

                <!--Add Confirm Password Textbox-->
                <div class="form-group">
                  <label for="confirmPassword">Confirm Password <span>*</span></label>
                  <div class="input-group">
                    <input
                        id="confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="form.confirmPassword"
                        @input="evaluateConfirmPassword"
                        required
                    />
                    <!--Password Show and Hide Button-->
                    <button type="button" style="width:50px; border: 1px solid #dee2e6; border-radius: 0 5px 5px 0; background-color: #fff;" @click="toggleShowConfirmPassword">
                      <i :class="showConfirmPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                    </button>
                  </div>
                  <!--Password Policy Checking and Password Strength Indicators-->
                  <div v-if="form.confirmPassword.length !== 0" class="progress mt-2" style="height: 5px; border-radius: 5px;">
                    <div :class="['progress-bar', confirmPasswordStrengthLabel === 'Strong' ? 'bg-success' : 'bg-danger']" :style="{ width: confirmPasswordStrength + '%' }"></div>
                  </div>
                  <div v-if="form.confirmPassword.length !== 0" class="d-flex justify-content-between mt-1">
                    <small :class="confirmPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-start">
                      {{ confirmPasswordErrors }}
                    </small>
                    <small :class="confirmPasswordStrengthLabel === 'Weak' ? 'text-danger' : 'text-success'" class="text-end">
                      {{ confirmPasswordStrengthLabel }}
                    </small>
                  </div>
                </div>

                <!--Change Password Button-->
                <div class="d-grid">
                  <button class="btn button-default btn-block w-100" :disabled="!canConfirm || loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-else>Confirm</span>
                  </button>
                </div>

                <!-- Copyright widget -->
                <small class="mt-4 mb-0 text-center text-xs d-block">
                  Copyright &copy; 2024. All Rights Reserved. <br> Developed by LAUGFS Group IT
                </small>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {resetPassword} from '@/services/dataService';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
  data() {
    return {
      //Main Variables
      form: {
        newPassword: "",
        confirmPassword: "",
        changePasswordError: ""
      },

      showNewPassword: false,
      showConfirmPassword: false,

      //Status Handling
      loading: false, // Loading state
      error: null, // Error state

      //Password Policy Checking
      newPasswordStrength: 0,
      newPasswordErrors: 'Password Strength: ',
      newPasswordStrengthLabel: 'Weak',
      confirmPasswordStrength: 0,
      confirmPasswordErrors: 'Password Strength: ',
      confirmPasswordStrengthLabel: 'Weak',
    };
  },

  setup() {
    const router = useRouter();
    const toast = useToast(); // Initialize the toaster
    return { router, toast };
  },

  computed: {
    //Password Change button active and inactive handling
    canConfirm() {
      return (
          this.form.newPassword &&
          this.form.confirmPassword &&
          this.form.newPassword === this.form.confirmPassword
      );
    },
  },

  methods: {
    //Hide and show new password
    toggleShowNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },

    //Hide and show confirm password
    toggleShowConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    //New Password Field Validations
    evaluateNewPassword() {
      const password = this.form.newPassword;
      const errors = [];
      let didMatchError = false;

      // Check criteria in order of priority and enqueue errors
      if (password.length < 10) {
        errors.push("Password must be at least 10 characters long.");
      }
      if (!/[a-z]/.test(password)) {
        errors.push("Password must include at least one lowercase letter.");
      }
      if (!/[A-Z]/.test(password)) {
        errors.push("Password must include at least one uppercase letter.");
      }
      if (!/[0-9]/.test(password)) {
        errors.push("Password must include at least one number (0-9).");
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push("Password must include at least one special character.");
      }
      if (password === this.form.currentPassword) {
        errors.push("New password should not be the same as the current password.");
        didMatchError = true; // Set flag for mismatch error
      }

      // Set the first error from the queue or a success message
      this.newPasswordErrors = errors.length ? errors[0] : "Password Policy Accepted.";

      // Update strength based on criteria met
      let strength = 0;
      if (password.length >= 10) strength += 20;
      if (/[a-z]/.test(password)) strength += 20;
      if (/[A-Z]/.test(password)) strength += 20;
      if (/[0-9]/.test(password)) strength += 20;
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20;

      this.newPasswordStrength = strength;
      this.newPasswordStrengthLabel = strength === 100 ? "Strong" : "Weak";
      this.newPasswordStrengthLabel = didMatchError ? "Weak" : strength === 100 ? "Strong" : "Weak";
    },

    //Confirm Password Field Validations
    evaluateConfirmPassword() {
      const password = this.form.confirmPassword;
      const errors = [];
      let mismatchError = false;
      let didMatchError = false;

      // Check criteria in order of priority and enqueue errors
      if (password.length < 10) {
        errors.push("Password must be at least 10 characters long.");
      }
      if (!/[a-z]/.test(password)) {
        errors.push("Password must include at least one lowercase letter.");
      }
      if (!/[A-Z]/.test(password)) {
        errors.push("Password must include at least one uppercase letter.");
      }
      if (!/[0-9]/.test(password)) {
        errors.push("Password must include at least one number (0-9).");
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push("Password must include at least one special character.");
      }
      if (password === this.form.currentPassword) {
        errors.push("New password should not be the same as the current password.");
        didMatchError = true; // Set flag for mismatch error
      }
      if (this.form.newPassword !== this.form.confirmPassword) {
        errors.push("New password and confirm password should be the same.");
        mismatchError = true; // Set flag for mismatch error
      }

      // Set the first error from the queue or a success message
      this.confirmPasswordErrors = errors.length ? errors[0] : "Password Policy Accepted.";

      // Update strength based on criteria met
      let strength = 0;
      if (password.length >= 10) strength += 20;
      if (/[a-z]/.test(password)) strength += 20;
      if (/[A-Z]/.test(password)) strength += 20;
      if (/[0-9]/.test(password)) strength += 20;
      if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20;

      this.confirmPasswordStrength = strength;
      this.confirmPasswordStrengthLabel = (didMatchError || mismatchError) ? "Weak" : strength === 100 ? "Strong" : "Weak";
    },

    //Password Change API Integration
    async handleChangePassword() {
      this.form.changePasswordError = ''; // change password Error message

      this.loading = true; // Set loading to true when reset password starts

      try {
        //  Retrieve token from localStorage
        const token = localStorage.getItem('token_set');

        // Retrieve mobile from localStorage
        const storedMobile = localStorage.getItem('mobile');

        // Retrieve otp from localStorage
        const storedOTP = localStorage.getItem('otp');

        // Reset Password API call
        const userResponse = await resetPassword(token, storedMobile, storedOTP, this.form.newPassword, this.form.confirmPassword);

        // Handle successful change password
        this.toast.success(userResponse.data.message);
        localStorage.removeItem("mobile");
        localStorage.removeItem("otp");
        this.$router.push('/login');
      } catch (error) {
        // Handle invalid credentials
        if (error.response && error.response.data && error.response.data.message) {
          this.form.changePasswordError = error.response.data.message;
          this.toast.error(this.form.changePasswordError); // Show toaster for login error
        } else {
          this.form.changePasswordError = "An error occurred. Please try again.";
          this.toast.error(this.form.changePasswordError); // Show toaster for general error
        }
        console.error('API Error:', error);
      } finally {
        this.loading = false; // Set loading to false when login completes
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
}

.form-container {
  background-color: #fff;
  padding: 30px 40px; /* 30px top/bottom padding, 40px left/right padding */
  border-radius: 20px; /* Adjust the border radius as needed */
}

.input-group .btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

i {
  font-size: 1.2rem;
  color: #000;
}

.btn {
  border: 1px solid #efefef;
  background-color: #fff;
}

.btn:hover {
  border: 1px solid #efefef;
  background-color: #fff;
}

a {
  text-decoration: none;
}

.progress {
  height: 5px;
  border-radius: 5px;
}

.progress-bar {
  transition: width 0.3s ease;
}

.d-flex {
  display: flex;
  justify-content: space-between;
}

.copyright {
  text-align: center;
  display: block;
}

</style>
